<template>
  <div class="overflow-x-auto lg:overflow-x-visible">
    <div class="min-w-[900px] lg:min-w-0">
      <div class="mb-3 max-w-md px-1">
        <input
          v-model.trim="searchQuery"
          type="search"
          class="ml-4 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
          :placeholder="searchPlaceholder"
        />
      </div>

      <table class="w-full table-fixed">
        <colgroup>
          <col class="w-[28%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
          <col class="w-[9%]" />
        </colgroup>
        <thead>
          <tr class="border-b border-gray-200 bg-gray-50">
            <th
              class="text-left py-3 px-4 text-sm font-semibold text-gray-500 min-w-48"
            >
              <Translation name="report.job_category" default="Job Category" />
            </th>
            <th
              class="text-right py-3 px-4 text-sm font-semibold text-gray-500"
            >
              <Translation name="report.workers" default="Workers" />
            </th>
            <th
              class="text-right py-3 px-4 text-sm font-semibold text-gray-500"
            >
              <Translation
                name="report.total_remuneration"
                default="Total Remuneration"
              />
            </th>
            <th
              class="text-right py-3 px-4 text-sm font-semibold text-gray-500"
            >
              <Translation name="report.wage" default="Wage" />
            </th>
            <th
              class="text-right py-3 px-4 text-sm font-semibold text-gray-500"
            >
              <Translation name="report.bonuses" default="Bonuses" />
            </th>
            <th
              class="text-right py-3 px-4 text-sm font-semibold text-gray-500"
            >
              <Translation
                name="report.in_kind_benefits"
                default="In-kind Benefits"
              />
            </th>
            <th
              class="text-right py-3 px-4 text-sm font-semibold text-gray-500"
            >
              <Translation name="report.living_wage" default="Living Wage" />
            </th>
            <th
              class="text-right py-3 px-4 text-sm font-semibold text-gray-500"
            >
              <Translation name="report.gap_currency" default="Gap" /> ({{
                currencyCode
              }})
            </th>
            <th
              class="text-right py-3 px-4 text-sm font-semibold text-gray-500"
            >
              <Translation name="report.gap_percentage" default="Gap (%)" />
            </th>
          </tr>
        </thead>
      </table>

      <div v-if="loading" class="border-b border-gray-100">
        <div class="py-4 px-4 text-sm text-gray-500">
          <Translation name="report.loading" default="Loading..." />
        </div>
      </div>

      <div
        v-else-if="filteredRows.length === 0"
        class="border-b border-gray-100"
      >
        <div class="py-4 px-4 text-sm text-gray-500">
          <Translation
            name="report.no_data_available"
            default="No data available"
          />
        </div>
      </div>

      <div
        v-else
        ref="scrollContainer"
        class="max-h-[32rem] overflow-y-auto"
        @scroll="onBodyScroll"
      >
        <table class="w-full table-fixed">
          <colgroup>
            <col class="w-[28%]" />
            <col class="w-[9%]" />
            <col class="w-[9%]" />
            <col class="w-[9%]" />
            <col class="w-[9%]" />
            <col class="w-[9%]" />
            <col class="w-[9%]" />
            <col class="w-[9%]" />
            <col class="w-[9%]" />
          </colgroup>
          <tbody>
            <tr v-if="topSpacerHeight > 0" aria-hidden="true">
              <td
                colspan="9"
                class="p-0 border-0"
                :style="{ height: `${topSpacerHeight}px` }"
              ></td>
            </tr>

            <tr
              v-for="(row, index) in visibleRows"
              :key="row.id || `${visibleStart + index}-${row.name}`"
              class="border-b border-gray-100 hover:bg-gray-50"
            >
              <td class="py-4 px-4">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-gray-600">{{
                    row.name
                  }}</span>
                </div>
              </td>
              <td class="py-4 px-4 text-right text-sm text-gray-600">
                {{ formatCount(row.workers) }}
              </td>
              <td class="py-4 px-4 text-right text-sm text-gray-600">
                {{ formatCurrency(row.total_remuneration) }}
              </td>
              <td class="py-4 px-4 text-right text-sm text-gray-600">
                {{ formatCurrency(row.wage) }}
              </td>
              <td class="py-4 px-4 text-right text-sm text-gray-600">
                {{ formatCurrency(row.bonuses) }}
              </td>
              <td class="py-4 px-4 text-right text-sm text-gray-600">
                {{ formatCurrency(row.ikb) }}
              </td>
              <td class="py-4 px-4 text-right text-sm text-gray-600">
                {{ formatCurrency(row.living_wage) }}
              </td>
              <td class="py-4 px-4 text-right text-sm" :class="gapClass(row)">
                {{ formatGapCurrency(row) }}
              </td>
              <td class="py-4 px-4 text-right text-sm" :class="gapClass(row)">
                {{ formatGapPercentage(row) }}
              </td>
            </tr>

            <tr v-if="bottomSpacerHeight > 0" aria-hidden="true">
              <td
                colspan="9"
                class="p-0 border-0"
                :style="{ height: `${bottomSpacerHeight}px` }"
              ></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useTranslation } from "./aft/useTranslations";

const props = defineProps({
  currencyCode: {
    type: String,
    required: true,
  },
});

const { _t } = useTranslation();
const searchPlaceholder = _t(
  "report.search_job_category",
  "Search job category",
);

const injectedRows = inject("livingWageGapCalculationJobCategoryRows", ref([]));
const injectedLoading = inject(
  "livingWageGapCalculationJobCategoryRowsLoading",
  ref(false),
);

const rows = computed(() => {
  return Array.isArray(injectedRows.value) ? injectedRows.value : [];
});
const loading = computed(() => Boolean(injectedLoading.value));

const scrollContainer = ref(null);
const scrollTop = ref(0);
const containerHeight = ref(512);
const searchQuery = ref("");
const rowHeight = 56;
const overscan = 8;

const filteredRows = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  if (query.length === 0) {
    return rows.value;
  }

  return rows.value.filter((row) => {
    const firstColumnValue = String(row?.name ?? "").toLowerCase();
    return firstColumnValue.includes(query);
  });
});

const visibleStart = computed(() =>
  Math.max(0, Math.floor(scrollTop.value / rowHeight) - overscan),
);

const visibleCount = computed(() =>
  Math.max(1, Math.ceil(containerHeight.value / rowHeight) + overscan * 2),
);

const visibleEnd = computed(() =>
  Math.min(filteredRows.value.length, visibleStart.value + visibleCount.value),
);

const visibleRows = computed(() =>
  filteredRows.value.slice(visibleStart.value, visibleEnd.value),
);

const topSpacerHeight = computed(() => visibleStart.value * rowHeight);
const bottomSpacerHeight = computed(
  () => (filteredRows.value.length - visibleEnd.value) * rowHeight,
);

function updateContainerHeight() {
  if (!scrollContainer.value) {
    return;
  }

  containerHeight.value = scrollContainer.value.clientHeight || 512;
}

function onBodyScroll(event) {
  scrollTop.value = event.target.scrollTop;
}

watch(
  () => filteredRows.value.length,
  (nextLength, prevLength) => {
    if (nextLength < prevLength) {
      scrollTop.value = 0;

      if (scrollContainer.value) {
        scrollContainer.value.scrollTop = 0;
      }
    }
  },
);

onMounted(() => {
  updateContainerHeight();
  window.addEventListener("resize", updateContainerHeight);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateContainerHeight);
});

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function formatNumber(value) {
  const num = toNumber(value);
  if (Number.isInteger(num)) {
    return `${num}`;
  }

  return num.toFixed(2).replace(/\.?0+$/, "");
}

function formatCount(value) {
  return formatNumber(value);
}

function formatCurrency(value) {
  return `${props.currencyCode} ${formatNumber(value)}`;
}

function rowHasGap(row) {
  if (typeof row?.has_gap === "boolean") {
    return row.has_gap;
  }

  return toNumber(row?.gap_percentage) > 0;
}

function gapClass(row) {
  return rowHasGap(row) ? "text-warning-700" : "text-success-700";
}

function formatGapCurrency(row) {
  if (!rowHasGap(row)) {
    return `${props.currencyCode} 0`;
  }

  return formatCurrency(row?.gap_currency);
}

function formatGapPercentage(row) {
  if (!rowHasGap(row)) {
    return "0%";
  }

  return `${formatNumber(row?.gap_percentage)}%`;
}
</script>
